import { useRef, useEffect } from "react";
const ClassicEditor = window.ClassicEditor;
import { BASE_URL_API } from "services/Http";

const UPLOAD_ENDPOINT = "upload";

/**
 * CustomUploadAdapter
 */
class CustomUploadAdapter {
    constructor(loader, dlid) {
        this.loader = loader;
        this.dlid = dlid;
    }

    upload() {
        return this.loader.file.then((file) => {
            return new Promise((resolve, reject) => {
                const baseUrlApi = BASE_URL_API.replace(/\/api$/, "");
                const formData = new FormData();
                formData.append("files", file);
                formData.append("baseUrlApi", baseUrlApi);

                fetch(`${baseUrlApi}/${UPLOAD_ENDPOINT}?dlid=${this.dlid}`, {
                    method: "POST",
                    body: formData,
                })
                    .then((response) => response.json())
                    .then((data) => {
                        resolve({ default: data.url });
                    })
                    .catch((error) => {
                        reject(error.message || "Image upload failed");
                    });
            });
        });
    }

    abort() {}
}

function uploadPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
        const dlid = editor.config.get("effectiveDlid");
        return new CustomUploadAdapter(loader, dlid);
    };
}

/**
 * NV2HtmlEditor (Targeted Vanilla Fix)
 * Bypasses the unstable CKEditor React component to avoid versioning conflicts.
 */
export default function NV2HtmlEditor({ value = "", onChange, height = 500, dlid }) {
    const containerRef = useRef(null);
    const editorRef = useRef(null);
    const effectiveDlid = dlid || localStorage.getItem("_dlid") || "";

    // Initialization and Cleanup
    useEffect(() => {
        if (!containerRef.current) return;

        ClassicEditor.create(containerRef.current, {
            effectiveDlid: effectiveDlid,
            extraPlugins: [uploadPlugin],
            toolbar: [
                "heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "|",
                "outdent", "indent", "|", "imageUpload", "blockQuote", "insertTable", "mediaEmbed", "undo", "redo"
            ],
        })
            .then((editor) => {
                editorRef.current = editor;
                editor.setData(value);

                // Set initial height
                const editableElement = editor.editing.view.document.getRoot();
                editor.editing.view.change((writer) => {
                    writer.setStyle("height", `${height}px`, editableElement);
                });

                // Listen for changes
                editor.model.document.on("change:data", () => {
                    if (onChange) {
                        onChange(editor.getData());
                    }
                });
            })
            .catch((error) => {
                console.error("[NV2HtmlEditor] Boot error:", error);
            });

        return () => {
            if (editorRef.current) {
                editorRef.current.destroy().catch(() => {});
                editorRef.current = null;
            }
        };
    }, []); // Run only once

    // Sync external height changes
    useEffect(() => {
        if (editorRef.current) {
            const editableElement = editorRef.current.editing.view.document.getRoot();
            editorRef.current.editing.view.change((writer) => {
                writer.setStyle("height", `${height}px`, editableElement);
            });
        }
    }, [height]);

    return (
        <div className="nv2-html-editor">
            <div ref={containerRef} />
        </div>
    );
}
